import React, { Fragment, useContext, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { RxCross2 } from "react-icons/rx";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import { getScreens, getRolePermissions, updateRolePermissions } from "../../service/Role";
import { toast } from "react-toastify";
import { Spinner } from "../../Spinner";

const PermissionModal = ({ open, onClose, role, onSaved }) => {
  const { theme, langMode } = useContext(AppContext);
  const [screensList, setScreensList] = useState([]);
  const [screenPermissions, setScreenPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-white mb-1";

  const roleId = role?.id ?? role?.roleId;
  const roleName = role?.name ?? role?.roleName ?? "Role";

  useEffect(() => {
    if (!open || !roleId) return;
    const load = async () => {
      setLoadingData(true);
      try {
        // Load screens list
        const screens = await getScreens();
        const screensArray = Array.isArray(screens) ? screens : [];
        setScreensList(screensArray);

        // Load role permissions
        let permsList = [];
        try {
          const permsResponse = await getRolePermissions(roleId);
          permsList = Array.isArray(permsResponse) ? permsResponse : [];
        } catch (e) {
          console.warn("Failed to load role permissions:", e);
          permsList = [];
        }

        // Initialize permissions object: start with all screens set to false
        const permsObj = {};
        screensArray.forEach((screen) => {
          if (screen.id != null) {
            permsObj[screen.id] = {
              canView: false,
              canCreate: false,
              canUpdate: false,
              canDelete: false,
            };
          }
        });

        // Map permissions from API response to screens by screenId
        permsList.forEach((perm) => {
          const screenId = perm.screenId;
          if (screenId != null && permsObj[screenId] != null) {
            permsObj[screenId] = {
              canView: Boolean(perm.canView),
              canCreate: Boolean(perm.canCreate),
              canUpdate: Boolean(perm.canUpdate),
              canDelete: Boolean(perm.canDelete),
            };
          }
        });

        setScreenPermissions(permsObj);
      } catch (err) {
        console.error(err);
        toast.error(translations[langMode].anerroroccurred || "Failed to load data");
        onClose();
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [open, roleId, onClose, langMode]);

  const handleSave = async () => {
    if (!roleId) return;
    setLoading(true);
    try {
      const permissions = screensList
        .map((screen) => {
          const perms = screenPermissions[screen.id] || {
            canView: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          };
          return {
            screenId: Number(screen.id),
            screenCode: String(screen.code ?? ""),
            screenName: String(screen.name ?? ""),
            canView: Boolean(perms.canView),
            canCreate: Boolean(perms.canCreate),
            canUpdate: Boolean(perms.canUpdate),
            canDelete: Boolean(perms.canDelete),
          };
        })
        .filter(
          (p) => p.canView || p.canCreate || p.canUpdate || p.canDelete
        );
      const payload = { permissions };
      const data = await updateRolePermissions(roleId, payload);
      if (data?.header?.code === 600 || data?.id != null) {
        toast.success("Permissions updated successfully");
        onSaved?.();
        onClose();
      } else {
        toast.error(data?.body?.value || "Failed to update permissions");
      }
    } catch (err) {
      toast.error(err?.response?.data?.body?.value || "Failed to update permissions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className={`relative rounded-2xl bg-white shadow-xl sm:max-w-lg w-full ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold">Add permission — {roleName}</Dialog.Title>
                  <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose}>
                    <RxCross2 size={20} />
                  </button>
                </div>
                <div className="px-5 py-6">
                  {loadingData ? (
                    <div className="flex justify-center py-8">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <label className={`${labelClass} block mb-2`}>Permissions for screens</label>
                      <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                        {screensList.length === 0 ? (
                          <p className="px-3 py-4 text-sm text-gray-500">No screens available</p>
                        ) : (
                          <ul className="divide-y divide-gray-100">
                            {screensList.map((screen) => {
                              const permissions = screenPermissions[screen.id] || {
                                canView: false,
                                canCreate: false,
                                canUpdate: false,
                                canDelete: false,
                              };
                              const isViewEnabled = permissions.canView;
                              const togglePermission = (permKey) => {
                                const newValue = !permissions[permKey];
                                setScreenPermissions((prev) => {
                                  const next = { ...prev, [screen.id]: { ...permissions, [permKey]: newValue } };
                                  if (permKey === "canView" && !newValue) {
                                    next[screen.id].canCreate = false;
                                    next[screen.id].canUpdate = false;
                                    next[screen.id].canDelete = false;
                                  }
                                  return next;
                                });
                              };
                              const permissionRows = [
                                { key: "canView", label: "View", disabled: false },
                                { key: "canCreate", label: "Create", disabled: !isViewEnabled },
                                { key: "canUpdate", label: "Update", disabled: !isViewEnabled },
                                { key: "canDelete", label: "Delete", disabled: !isViewEnabled },
                              ];
                              return (
                                <li key={screen.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                  <div className="mb-2">
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 block truncate">{screen.name}</span>
                                    {screen.description && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400 block truncate">{screen.description}</span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-4">
                                    {permissionRows.map(({ key, label, disabled }) => (
                                      <div key={key} className={`flex items-center gap-2 ${disabled ? "opacity-60" : ""}`}>
                                        <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
                                        <button
                                          type="button"
                                          role="switch"
                                          aria-checked={permissions[key]}
                                          aria-disabled={disabled}
                                          disabled={disabled}
                                          onClick={() => !disabled && togglePermission(key)}
                                          className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${disabled ? "cursor-not-allowed bg-gray-200 dark:bg-gray-600" : "cursor-pointer"} ${!disabled && permissions[key] ? "bg-blue-600" : !disabled ? "bg-gray-300 dark:bg-gray-600" : ""}`}
                                        >
                                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${permissions[key] ? "translate-x-5" : "translate-x-0.5"}`} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex justify-end items-center gap-3 px-5 py-4 border-t border-gray-200">
                  <CommonButton variant="danger" onClick={onClose}>{translations[langMode].cancle || "Cancel"}</CommonButton>
                  <CommonButton variant="primary" onClick={handleSave} disabled={loading || loadingData}>{translations[langMode].save}</CommonButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PermissionModal;
